/**
 * Vuex Store for Users settings
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2018
 */

import { UsersApi } from '@/services/api-users.js';
import { InvitationsApi } from '@/services/api-invitations.js';

function defaultStateFactory() {
  return {
    users: [],
    pagination: {},
    user: {
      roles: [],
    },
    teamRoles: [
      { label: 'Owner', value: 'Owner' },
      { label: 'User', value: 'User' },
      { label: 'Viewer', value: 'Viewer' },
    ],
  };
}

export const state = defaultStateFactory();

export const getters = {
  userById: (state) => (id) => state.users.find((user) => user.id === id),
  userIndex: (state) => (id) => state.users.findIndex((user) => user.id === id),
};

export const mutations = {
  SET_USERS(state, { meta = {}, data = [] }) {
    state.pagination = meta;
    state.users = data;
  },

  SET_USER(state, user = {}) {
    state.user = user;
  },

  DELETE_USER(state, { index = -1 }) {
    state.user = {};
    // remove from the collection if it exists
    if (index > -1) {
      state.users.splice(index, 1);
    }
  },

  RESET_STATE(state, defaultState) {
    state = defaultState;
  },
};

export const actions = {
  fetch({ commit, state }, { query }) {
    return UsersApi.getUsers(query)
      .then((users) => {
        commit('SET_USERS', users);
        return state.users;
      })
      .catch((err) => {
        commit('SET_USERS', {});
        throw err;
      });
  },

  read({ commit, getters, state }, { id }) {
    // do we already have the user in the list
    const user = getters.userById(id);
    if (user) {
      commit('SET_USER', user);
      return Promise.resolve(user);
    }

    // if not fetch it from the server
    return UsersApi.getUser(id)
      .then((user) => {
        commit('SET_USER', user);
        return state.user;
      })
      .catch((err) => {
        commit('SET_USER', {});
        throw err;
      });
  },

  update({ commit, state }, { id, data }) {
    return UsersApi.updateUser(id, data).then((user) => {
      commit('SET_USER', user);
      return state.user;
    });
  },

  delete({ commit, getters }, { id }) {
    return UsersApi.deleteUser(id).then((response) => {
      commit('DELETE_USER', {
        index: getters.userIndex(id),
      });
      return response;
    });
  },

  invite(context, { data }) {
    return InvitationsApi.createInvite(data).then((invitation) => {
      return invitation;
    });
  },

  /**
   * Sets the state back to the default values
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('RESET_STATE', defaultStateFactory());
  },
};
